import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Chip, IconButton,
  Collapse, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Tooltip, LinearProgress, useTheme, Card, CardContent, Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon, CheckCircle as PassIcon, Error as FailIcon,
  Warning as WarnIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon,
  Build as FixIcon, AccountBalance, Inventory, PriceCheck, Description,
  AccountBalanceWallet, Settings, Shield as ShieldIcon,
  PointOfSale, Business,
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Print as PrintIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import OrphanBatchFixDialog from '../../components/dialogs/OrphanBatchFixDialog';
import FifoFixDialog from '../../components/dialogs/FifoFixDialog';

interface Detail { documentNumber: string; description: string; expectedValue?: number; actualValue?: number; difference?: number; navigationPath?: string; itemCode?: string; itemName?: string; location?: string; }
interface Check { checkId: number; checkName: string; checkNameAr: string; status: string; issueCount: number; message: string; messageAr: string; canAutoFix: boolean; fixAction: string; details: Detail[]; }
interface Section { sectionName: string; sectionNameAr: string; icon: string; totalChecks: number; passedChecks: number; checks: Check[]; }
interface Report { checkedAt: string; totalChecks: number; passedChecks: number; failedChecks: number; sections: Section[]; }

const DataIntegrityReportPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [expandedChecks, setExpandedChecks] = useState<Record<number, boolean>>({});
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [fifoDialogOpen, setFifoDialogOpen] = useState(false);

  useEffect(() => { companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {}); }, []);

  const runCheck = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/DataIntegrity/check');
      setReport(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to run check');
    } finally {
      setLoading(false);
    }
  };

  const autoFix = async (fixAction: string) => {
    setFixing(fixAction);
    try {
      const { data } = await axiosInstance.post('/DataIntegrity/fix', { fixAction });
      if (data.isSuccess) {
        toast.success(isRTL ? `تم إصلاح ${data.fixedCount} سجل` : `Fixed ${data.fixedCount} records`);
        await runCheck();
      } else {
        toast.error(data.message);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Fix failed');
    } finally {
      setFixing(null);
    }
  };

  const toggleSection = (name: string) => setExpanded(p => ({ ...p, [name]: !p[name] }));
  const toggleCheck = (id: number) => setExpandedChecks(p => ({ ...p, [id]: !p[id] }));

  const getIcon = (iconName: string) => {
    const icons: Record<string, React.ReactNode> = {
      AccountBalance: <AccountBalance />, Inventory: <Inventory />,
      PriceCheck: <PriceCheck />, Description: <Description />,
      AccountBalanceWallet: <AccountBalanceWallet />, Settings: <Settings />,
      PointOfSale: <PointOfSale />, Business: <Business />,
    };
    return icons[iconName] || <ShieldIcon />;
  };

  const statusColor = (status: string) => status === 'Pass' ? '#4caf50' : status === 'Warning' ? '#ff9800' : '#f44336';
  const statusIcon = (status: string) => status === 'Pass' ? <PassIcon sx={{ color: '#4caf50', fontSize: 20 }} /> : status === 'Warning' ? <WarnIcon sx={{ color: '#ff9800', fontSize: 20 }} /> : <FailIcon sx={{ color: '#f44336', fontSize: 20 }} />;

  const sectionGradient = (passed: number, total: number) => {
    if (passed === total) return 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)';
    if (passed >= total * 0.7) return 'linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)';
    return 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)';
  };

  const reportTitle = isRTL ? 'تقرير سلامة البيانات' : 'Data Integrity Report';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;

  const getAllChecks = () => {
    if (!report) return [];
    const rows: { section: string; check: string; status: string; issues: number; message: string }[] = [];
    report.sections.forEach(s => s.checks.forEach(c => rows.push({
      section: isRTL ? s.sectionNameAr : s.sectionName,
      check: isRTL ? c.checkNameAr : c.checkName,
      status: c.status, issues: c.issueCount,
      message: isRTL ? c.messageAr : c.message,
    })));
    return rows;
  };

  const exportToExcel = async () => {
    if (!report) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) { try { const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' }); ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } }); } catch {} }
      ws.mergeCells('C1:F1'); const cc = ws.getCell('C1'); cc.value = compName || ''; cc.font = { size: 18, bold: true, color: { argb: 'FF667eea' } }; cc.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.mergeCells('A3:F3'); const tc = ws.getCell('A3'); tc.value = reportTitle; tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; tc.alignment = { horizontal: 'center' }; tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
      ws.mergeCells('A4:F4'); const sc = ws.getCell('A4'); sc.value = `${isRTL ? 'ناجح' : 'Passed'}: ${report.passedChecks}/${report.totalChecks} | ${isRTL ? 'مشاكل' : 'Issues'}: ${report.failedChecks}`; sc.alignment = { horizontal: 'center' };
      const headers = [isRTL ? 'القسم' : 'Section', isRTL ? 'الفحص' : 'Check', isRTL ? 'الحالة' : 'Status', isRTL ? 'المشاكل' : 'Issues', isRTL ? 'الرسالة' : 'Message'];
      const hr = ws.getRow(6); headers.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } }; c.alignment = { horizontal: 'center' }; });
      getAllChecks().forEach((r, i) => { const row = ws.getRow(7 + i); row.getCell(1).value = r.section; row.getCell(2).value = r.check; row.getCell(3).value = r.status; row.getCell(4).value = r.issues; row.getCell(5).value = r.message;
        const bg = r.status === 'Pass' ? 'FFE8F5E9' : r.status === 'Warning' ? 'FFFFF3E0' : 'FFFFEBEE';
        row.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }; }); });
      ws.columns = [{ width: 20 }, { width: 45 }, { width: 10 }, { width: 10 }, { width: 30 }];
      const buf = await wb.xlsx.writeBuffer(); const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `DataIntegrity_${Date.now()}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const exportToPdf = async () => {
    if (!report) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.width;
      doc.setFillColor(245, 240, 250); doc.rect(0, 0, pw, 45, 'F');
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(102, 126, 234);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(102, 126, 234); doc.roundedRect(pw / 2 - 50, 32, 100, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255); doc.text(reportTitle, pw / 2, 38.5, { align: 'center' });
      const rows = getAllChecks();
      autoTable(doc, { head: [[isRTL ? 'القسم' : 'Section', isRTL ? 'الفحص' : 'Check', isRTL ? 'الحالة' : 'Status', isRTL ? 'المشاكل' : 'Issues', isRTL ? 'الرسالة' : 'Message']], body: rows.map(r => [r.section, r.check, r.status, r.issues.toString(), r.message]), startY: 50, theme: 'grid',
        headStyles: { fillColor: [102, 126, 234], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 8, textColor: [50, 50, 50] },
        didParseCell: (data: any) => { if (data.section === 'body') { const s = rows[data.row.index]?.status; if (s === 'Pass') data.cell.styles.fillColor = [232, 245, 233]; else if (s === 'Warning') data.cell.styles.fillColor = [255, 243, 224]; else if (s === 'Fail') data.cell.styles.fillColor = [255, 235, 238]; } },
      });
      doc.save(`DataIntegrity_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const handlePrint = () => {
    if (!report) return;
    const rows = getAllChecks();
    const w = window.open('', '_blank', 'width=1000,height=700');
    if (!w) return;
    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:20px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #667eea;padding-bottom:10px;margin-bottom:15px}
      .header img{max-height:60px} .header h1{color:#667eea;margin:0;font-size:22px} .header .sub{color:#666;font-size:12px}
      .summary{display:flex;gap:15px;margin-bottom:15px} .summary .card{padding:10px 20px;border-radius:8px;color:white;text-align:center;flex:1}
      table{width:100%;border-collapse:collapse;font-size:11px} th{background:#667eea;color:white;padding:8px;text-align:${isRTL ? 'right' : 'left'}}
      td{padding:6px 8px;border:1px solid #ddd} .pass{background:#e8f5e9} .warn{background:#fff3e0} .fail{background:#ffebee}
      @media print{body{margin:10px} .summary .card{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header"><div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
    <div style="text-align:center;flex:1"><h1>${reportTitle}</h1><div class="sub">${compName || ''} | ${companyInfo?.address || ''} | ${companyInfo?.phone || ''}</div></div><div style="width:80px"></div></div>
    <div class="summary"><div class="card" style="background:#667eea"><div style="font-size:12px">${isRTL ? 'إجمالي' : 'Total'}</div><div style="font-size:28px;font-weight:bold">${report.totalChecks}</div></div>
    <div class="card" style="background:#4caf50"><div style="font-size:12px">${isRTL ? 'ناجح' : 'Passed'}</div><div style="font-size:28px;font-weight:bold">${report.passedChecks}</div></div>
    <div class="card" style="background:${report.failedChecks > 0 ? '#f44336' : '#4caf50'}"><div style="font-size:12px">${isRTL ? 'مشاكل' : 'Issues'}</div><div style="font-size:28px;font-weight:bold">${report.failedChecks}</div></div></div>
    <table><thead><tr><th>${isRTL ? 'القسم' : 'Section'}</th><th>${isRTL ? 'الفحص' : 'Check'}</th><th>${isRTL ? 'الحالة' : 'Status'}</th><th>${isRTL ? 'المشاكل' : 'Issues'}</th><th>${isRTL ? 'الرسالة' : 'Message'}</th></tr></thead><tbody>
    ${rows.map(r => `<tr class="${r.status === 'Pass' ? 'pass' : r.status === 'Warning' ? 'warn' : 'fail'}"><td>${r.section}</td><td>${r.check}</td><td>${r.status}</td><td>${r.issues}</td><td>${r.message}</td></tr>`).join('')}
    </tbody></table><div style="text-align:center;margin-top:15px;color:#999;font-size:10px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {isRTL ? 'تقرير سلامة البيانات' : 'Data Integrity Report'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL ? 'فحص شامل لجميع البيانات والتسعير والمحاسبة' : 'Comprehensive check of all data, costing, and accounting'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={runCheck} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', px: 3, py: 1, borderRadius: 3 }}>
            {loading ? (isRTL ? 'جاري الفحص...' : 'Checking...') : (isRTL ? 'فحص الآن' : 'Run Check')}
          </Button>
          {report && (<>
            <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel} sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>{isRTL ? 'Excel' : 'Excel'}</Button>
            <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf} sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>{isRTL ? 'طباعة' : 'Print'}</Button>
          </>)}
        </Box>
      </Box>

      {/* Summary Cards */}
      {report && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'إجمالي الفحوصات' : 'Total Checks'}</Typography>
              <Typography variant="h3" fontWeight="bold">{report.totalChecks}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'ناجح' : 'Passed'}</Typography>
              <Typography variant="h3" fontWeight="bold">{report.passedChecks}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ background: report.failedChecks > 0 ? 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'مشاكل' : 'Issues'}</Typography>
              <Typography variant="h3" fontWeight="bold">{report.failedChecks}</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {report && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          {isRTL ? 'آخر فحص:' : 'Last check:'} {new Date(report.checkedAt).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}
        </Typography>
      )}

      {/* Sections */}
      {report?.sections.map((section) => (
        <Paper key={section.sectionName} elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
          {/* Section Header */}
          <Box onClick={() => toggleSection(section.sectionName)}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, cursor: 'pointer',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              '&:hover': { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' } }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: sectionGradient(section.passedChecks, section.totalChecks), color: 'white' }}>
              {getIcon(section.icon)}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight="bold">{isRTL ? section.sectionNameAr : section.sectionName}</Typography>
              <LinearProgress variant="determinate" value={(section.passedChecks / section.totalChecks) * 100}
                sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': { background: sectionGradient(section.passedChecks, section.totalChecks), borderRadius: 3 } }} />
            </Box>
            <Chip label={`${section.passedChecks}/${section.totalChecks}`} size="small"
              sx={{ bgcolor: statusColor(section.passedChecks === section.totalChecks ? 'Pass' : 'Fail'), color: 'white', fontWeight: 'bold' }} />
            {expanded[section.sectionName] ? <CollapseIcon /> : <ExpandIcon />}
          </Box>

          {/* Section Checks */}
          <Collapse in={expanded[section.sectionName]}>
            <Box sx={{ p: 0 }}>
              {section.checks.map((check) => (
                <Box key={check.checkId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)' } }}>
                    {statusIcon(check.status)}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>{isRTL ? check.checkNameAr : check.checkName}</Typography>
                      <Typography variant="caption" color="text.secondary">{isRTL ? check.messageAr : check.message}</Typography>
                    </Box>
                    {check.issueCount > 0 && (
                      <Chip label={check.issueCount} size="small" color={check.status === 'Warning' ? 'warning' : 'error'} />
                    )}
                    {check.canAutoFix && (
                      <Tooltip title={isRTL ? 'إصلاح تلقائي' : 'Auto Fix'}>
                        <Button size="small" variant="outlined" color="warning" startIcon={fixing === check.fixAction ? <CircularProgress size={14} /> : <FixIcon />}
                          onClick={() => autoFix(check.fixAction)} disabled={!!fixing}
                          sx={{ borderRadius: 2, minWidth: 'auto', px: 1.5 }}>
                          {isRTL ? 'إصلاح' : 'Fix'}
                        </Button>
                      </Tooltip>
                    )}
                    {check.checkId === 20 && check.issueCount > 0 && (
                      <Tooltip title={isRTL ? 'إصلاح متقدم: كشف + تحقق + تطبيق' : 'Advanced fix: Detect + Inspect + Apply'}>
                        <Button size="small" variant="contained" startIcon={<FixIcon />}
                          onClick={() => setOrphanDialogOpen(true)}
                          sx={{
                            borderRadius: 2, minWidth: 'auto', px: 1.5,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #663d8f 100%)' }
                          }}>
                          🔧 {isRTL ? 'إصلاح متقدم' : 'Advanced Fix'}
                        </Button>
                      </Tooltip>
                    )}
                    {check.checkId === 16 && check.issueCount > 0 && (
                      <Tooltip title={isRTL ? 'إصلاح متقدم: كشف + تحقق + تطبيق ترتيب FIFO' : 'Advanced FIFO order fix'}>
                        <Button size="small" variant="contained" startIcon={<FixIcon />}
                          onClick={() => setFifoDialogOpen(true)}
                          sx={{
                            borderRadius: 2, minWidth: 'auto', px: 1.5,
                            background: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
                            color: 'white',
                            '&:hover': { background: 'linear-gradient(135deg, #d11f10 0%, #d59b17 100%)' }
                          }}>
                          🔧 {isRTL ? 'إصلاح ترتيب FIFO' : 'Fix FIFO Order'}
                        </Button>
                      </Tooltip>
                    )}
                    {check.details.length > 0 && (
                      <IconButton size="small" onClick={() => toggleCheck(check.checkId)}>
                        {expandedChecks[check.checkId] ? <CollapseIcon /> : <ExpandIcon />}
                      </IconButton>
                    )}
                  </Box>

                  {/* Check Details */}
                  <Collapse in={expandedChecks[check.checkId]}>
                    {check.details.length > 0 && (
                      <TableContainer sx={{ px: 3, pb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'المستند' : 'Document'}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'التفاصيل' : 'Details'}</TableCell>
                              {check.details.some(d => d.actualValue != null) && (
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{isRTL ? 'الفعلي' : 'Actual'}</TableCell>
                              )}
                              {check.details.some(d => d.expectedValue != null) && (
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{isRTL ? 'المتوقع' : 'Expected'}</TableCell>
                              )}
                              {check.details.some(d => d.navigationPath) && (
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'فتح' : 'Open'}</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {check.details.map((d, i) => (
                              <TableRow key={i} hover>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{d.documentNumber}</TableCell>
                                <TableCell>
                                  <Typography variant="body2">{d.description}</Typography>
                                  {(d.itemCode || d.location) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                                      {d.itemCode && <span>📦 {d.itemCode}</span>}
                                      {d.itemCode && d.location && <span> · </span>}
                                      {d.location && <span>📍 {d.location}</span>}
                                    </Typography>
                                  )}
                                </TableCell>
                                {check.details.some(x => x.actualValue != null) && (
                                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                    {d.actualValue?.toFixed(3) ?? '-'}
                                  </TableCell>
                                )}
                                {check.details.some(x => x.expectedValue != null) && (
                                  <TableCell align="right">{d.expectedValue?.toFixed(3) ?? '-'}</TableCell>
                                )}
                                {check.details.some(x => x.navigationPath) && (
                                  <TableCell align="center">
                                    {d.navigationPath ? (
                                      <Tooltip title={isRTL ? 'افتح المستند في تاب جديد' : 'Open document in new tab'}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => window.open(d.navigationPath!, '_blank', 'noopener,noreferrer')}
                                        >
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">—</Typography>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Collapse>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Paper>
      ))}

      {!report && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <ShieldIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {isRTL ? 'اضغط "فحص الآن" لبدء فحص سلامة البيانات' : 'Click "Run Check" to start data integrity check'}
          </Typography>
        </Paper>
      )}

      <OrphanBatchFixDialog
        open={orphanDialogOpen}
        onClose={() => setOrphanDialogOpen(false)}
        isRTL={isRTL}
        onFixComplete={() => { runCheck(); }}
      />
      <FifoFixDialog
        open={fifoDialogOpen}
        onClose={() => setFifoDialogOpen(false)}
        isRTL={isRTL}
        onFixComplete={() => { runCheck(); }}
      />
    </Box>
  );
};

export default DataIntegrityReportPage;
